import StyleConstants from '../../../core/styles.constants';
import {
  isAsDrawn,
  type Coordinate,
  type DeskFeature,
  type EditableFloorPlan,
  type IconLabelFeature,
  type MultiPolygonCoordinates,
  type TextLabelFeature,
} from '../models/editor-feature';
import { backdropDrawOrder } from './backdrop-order';
import { layerPalette } from './editor-theme';
import { computeBoundingBox } from './projection';
import { selectionKeyOf } from './selection';

/**
 * What the editor surface should show, as data.
 *
 * Separated from the painting so the two questions can be asked apart: *what* is drawn, in
 * *what order*, with *what fill* — which is all behaviour, and all of it worth pinning — and
 * then the mechanical business of pushing it into a 2D context, which is not.
 *
 * It also outlived the renderer it was extracted from. These rules were previously asserted
 * through the SVG DOM, and moving to a canvas would otherwise have deleted every one of those
 * tests along with the elements they queried: draw order, the floor-plate fill ramp, whether a
 * retired desk is drawn, whether a line-only floor is drawn at all. Each of those is a real
 * defect somebody already shipped, and none of them is visible in a canvas after the fact —
 * a plan painted in the wrong order looks exactly like a plan that failed to load.
 *
 * Coordinates stay in the plan's own drawing units. Projecting them is the painter's job, so
 * the same plan yields the same commands at every zoom.
 */

export type DrawLayerTag = 'floor' | 'room' | 'border' | 'deskShape' | 'desk' | 'label';

interface CommandBase {
  readonly id: string;
  readonly layer: DrawLayerTag;
}

/** A filled and/or stroked shape: the plate, a wall, a room, an outline, a desk drawing. */
export interface AreaCommand extends CommandBase {
  readonly kind: 'area';
  readonly coordinates: MultiPolygonCoordinates;
  readonly fill: string | null;
  readonly fillOpacity: number;
  readonly stroke: string | null;
  readonly strokeWidth: number;
}

/** Floor detail stored as runs of points rather than as closed shapes. */
export interface PolylineCommand extends CommandBase {
  readonly kind: 'polyline';
  readonly paths: readonly Coordinate[][];
  readonly stroke: string;
  readonly strokeWidth: number;
}

export interface DeskCommand extends CommandBase {
  readonly kind: 'desk';
  readonly at: Coordinate;
  /** Degrees clockwise. Drawn, never applied to the coordinates — a desk has no footprint. */
  readonly rotation: number;
  readonly radius: number;
  readonly fill: string;
  readonly stroke: string;
  readonly strokeWidth: number;
  /** Out of service: hollow and dashed rather than a third colour. */
  readonly dashed: boolean;
  readonly pod: boolean;
  /**
   * An MDI path to draw this desk as, instead of the round marker.
   *
   * Null covers three different things — no icon chosen, a name that is not in the set, and
   * the set not having loaded yet — and all three draw the marker, which is the right answer
   * for each: a desk must always be visible, whatever its icon turns out to be.
   */
  readonly iconPath: string | null;
  /** How wide that icon may be drawn, in surface units, so it stays on its own desk. */
  readonly iconSize: number;
}

export interface TextCommand extends CommandBase {
  readonly kind: 'text';
  readonly at: Coordinate;
  readonly rotation: number;
  readonly text: string;
  readonly fontSize: number;
  readonly color: string;
  readonly outline: string | null;
  readonly outlineWidth: number;
}

export interface IconCommand extends CommandBase {
  readonly kind: 'icon';
  readonly at: Coordinate;
  readonly rotation: number;
  /** An SVG path from the MDI set, or null when the name is not in it. */
  readonly path: string | null;
  readonly size: number;
  readonly color: string;
  readonly outlined: boolean;
  /**
   * Whether to draw the "this icon name is not real" marker.
   *
   * Distinct from `path === null`: before the icon set has loaded, every name looks unknown,
   * and saying so then would flash a placeholder over every valid icon on the plan.
   */
  readonly placeholder: boolean;
}

export type DrawCommand = AreaCommand | PolylineCommand | DeskCommand | TextCommand | IconCommand;

/** The slab a plan is drawn on: near-white, so the walls standing on it are what the eye reads. */
export const PLATE_FILL = layerPalette.plate;
const PLATE_STROKE = layerPalette.plateEdge;

/** Walls and structural fragments: solid enough to read as material at a glance. */
export const WALL_FILL = layerPalette.wall;
const WALL_STROKE = layerPalette.wallEdge;

/** What is kept as drawn from a traced picture: its ink, with no outline of its own. */
export const AS_DRAWN_FILL = layerPalette.drawing;

/**
 * Rooms sit in front of the walls, so their fill is translucent and their outline is not.
 * The outline is what marks the room out; the fill only has to tint what it covers.
 */
const ROOM_FILL = layerPalette.room;
const ROOM_FILL_OPACITY = layerPalette.roomOpacity;
const ROOM_STROKE = layerPalette.roomEdge;

const DESK_SHAPE_FILL = layerPalette.deskShape;
const DESK_SHAPE_STROKE = layerPalette.deskShapeEdge;
const HIGHLIGHT_FILL = layerPalette.accentTint;

/**
 * The desk marker's radius, in surface units, on a plan with no scale at all.
 *
 * A fixed number because there is nothing to derive one from. Everywhere else the marker is
 * sized from the real world instead — see `deskRadiusOf`.
 */
const DESK_RADIUS = 5;

/**
 * The most floor a desk's icon may cover, in metres.
 *
 * A catalogue desk is 0.8 m deep, and the glyph replacing its marker is drawn several times
 * the marker's radius so it reads at the same weight. Uncapped that came out wider than the
 * desk itself, so an icon spilled across the desk beside it and a stamped group looked like a
 * row of overlapping symbols. Kept just inside a desk's depth.
 */
const DESK_ICON_METRES = 0.7;

/**
 * How big a desk marker is in real terms: a 0.7 m dot, about the size of a chair.
 *
 * Derived rather than fixed because a fixed radius means something different on every plan.
 * These coordinates are each project's own units, and the editor fits whatever extent it
 * finds into one 1200x800 surface — so five surface units is a dot on a floor of twenty desks
 * and a blob covering four rooms on a site plan of six buildings.
 */
const DESK_RADIUS_METRES = 0.35;

/** Kept inside this, in surface units, so a marker is neither invisible nor a blot. */
const MINIMUM_DESK_RADIUS = 3;
const MAXIMUM_DESK_RADIUS = 14;

export interface DrawPlanOptions {
  readonly showFloor: boolean;
  readonly showRooms: boolean;
  readonly showBorder: boolean;
  readonly showDesks: boolean;
  readonly showDeskShapes: boolean;
  readonly showLabels: boolean;
  /** Whether desks already out of service are drawn at all. */
  readonly showRetiredDesks: boolean;
  readonly selectedKeys: ReadonlySet<string>;
  readonly hoveredKey?: string;
  readonly retiredDeskIds?: ReadonlySet<string>;
  readonly unbookableDeskIds?: readonly string[];
  /** Resolves an MDI slug to its path, or undefined when the name is not in the set. */
  readonly iconPathOf: (name: string | null) => string | undefined;
  readonly iconsLoaded: boolean;
  /**
   * Surface units per plan unit: the projection's own scale.
   *
   * Needed because a desk outline is measured in the plan's units and drawn in the surface's,
   * and nothing else in this file has any way to convert between them.
   */
  readonly planScale: number;
  /** Plan units per metre, or null on a plan with no scale — measured or estimated. */
  readonly unitsPerMetre: number | null;
}

/**
 * A length in the plan's own units, as surface units.
 *
 * The one conversion this file has to get right. Everything drawn here is authored in surface
 * units — the 1200x800 space the viewport transform works in — while the plan's geometry is
 * in its own arbitrary units, and the two differ by whatever ratio it took to fit the plan on
 * the surface. Mixing them produced furniture icons a fifth of a pixel across.
 */
const toSurface = (planUnits: number, options: DrawPlanOptions): number => planUnits * options.planScale;

/**
 * How much bigger an icon is drawn than the marker it replaces.
 *
 * A glyph reads at roughly half the weight of a solid disc of the same size — most of an
 * icon's box is empty — so matching the radius exactly would make every iconed desk look
 * smaller than the plain ones beside it.
 */
const DESK_ICON_SCALE = 3.2;

const deskRadiusOf = (options: DrawPlanOptions): number => {
  if (!options.unitsPerMetre) {
    return DESK_RADIUS;
  }

  const radius = toSurface(DESK_RADIUS_METRES * options.unitsPerMetre, options);

  return Math.min(MAXIMUM_DESK_RADIUS, Math.max(MINIMUM_DESK_RADIUS, radius));
};

/**
 * How big a desk's icon is drawn, in surface units.
 *
 * Several times the marker's radius, so a glyph reads at the weight a solid disc does — but
 * never more than a desk's own depth, which is what stops it covering its neighbour.
 */
const deskIconSizeOf = (options: DrawPlanOptions): number => {
  const fromMarker = deskRadiusOf(options) * DESK_ICON_SCALE;

  if (!options.unitsPerMetre) {
    return fromMarker;
  }

  return Math.min(fromMarker, toSurface(DESK_ICON_METRES * options.unitsPerMetre, options));
};

/** Picks the emphasis a state deserves: selected reads strongest, hovered a hint below it. */
const emphasis = (selected: boolean, hovered: boolean, whenSelected: number, whenHovered: number, otherwise: number): number => {
  if (selected) return whenSelected;
  if (hovered) return whenHovered;
  return otherwise;
};

/** A shape's bounding box, which is the footprint an icon drawn in its place has to fill. */
const extentOfRings = (
  coordinates: readonly (readonly (readonly Coordinate[])[])[],
): { minX: number; minY: number; maxX: number; maxY: number } => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  })));

  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : {
    minX: 0, minY: 0, maxX: 0, maxY: 0,
  };
};

/**
 * A desk's fill.
 *
 * Out of service reads as hollow rather than as a third colour: a retired desk is not a
 * warning to act on, it is an absence — and it is only drawn at all because the administrator
 * asked to see it.
 */
const deskFill = (retired: boolean, bookable: boolean): string => {
  if (retired) return StyleConstants.WHITE_COLOR;

  return bookable ? layerPalette.desk : layerPalette.deskUnbookable;
};

const deskCommandOf = (desk: DeskFeature, options: DrawPlanOptions): DeskCommand => {
  const key = selectionKeyOf(desk);
  const selected = options.selectedKeys.has(key);
  const hovered = options.hoveredKey === key;
  const retired = options.retiredDeskIds?.has(desk.featureId) ?? false;
  const iconScale = desk.iconScale ?? 1;

  return {
    kind: 'desk',
    layer: 'desk',
    id: desk.id,
    at: desk.coordinates,
    rotation: desk.rotation,
    radius: deskRadiusOf(options) * iconScale * (hovered && !selected ? 1.3 : 1),
    fill: deskFill(retired, !options.unbookableDeskIds?.includes(desk.featureId)),
    stroke: selected || hovered ? layerPalette.selection : layerPalette.deskEdge,
    strokeWidth: emphasis(selected, hovered, 2, 1.5, 0.8),
    dashed: retired,
    pod: desk.deskType === 'pod',
    iconPath: (desk.iconName ? options.iconPathOf(desk.iconName) : undefined) ?? null,
    iconSize: deskIconSizeOf(options) * iconScale,
  };
};

const labelCommandOf = (
  label: TextLabelFeature | IconLabelFeature,
  options: DrawPlanOptions,
): TextCommand | IconCommand => {
  const key = selectionKeyOf(label);
  const selected = options.selectedKeys.has(key);
  const hovered = options.hoveredKey === key;

  if (label.kind === 'textLabel') {
    return {
      kind: 'text',
      layer: 'label',
      id: label.id,
      at: label.coordinates,
      rotation: label.rotation,
      text: label.text,
      fontSize: label.fontSize,
      color: label.color,
      outline: selected || hovered ? layerPalette.selection : null,
      outlineWidth: emphasis(selected, hovered, 0.5, 0.3, 0),
    };
  }

  const path = options.iconPathOf(label.iconName) ?? null;

  return {
    kind: 'icon',
    layer: 'label',
    id: label.id,
    at: label.coordinates,
    rotation: label.rotation,
    path,
    size: label.iconSize,
    color: label.color,
    outlined: selected || hovered,
    // An icon name outside this MDI major draws nothing at all on the mobile app, with no
    // error anywhere — the editor is the only place that mistake is ever visible. But it can
    // only be said once the set it would be looked up in has actually arrived.
    placeholder: path === null && options.iconsLoaded,
  };
};

export const buildDrawPlan = (
  floorPlan: EditableFloorPlan,
  options: DrawPlanOptions,
): DrawCommand[] => {
  const commands: DrawCommand[] = [];

  if (options.showFloor) {
    // Size decides painting order and fill, so a floor plate ends up under the walls rather
    // than over them. Which of the two a stored layer happens to produce is otherwise pure
    // luck: the plans that read correctly list their plate first, and the ones that came up
    // blank listed it last.
    // What is kept as drawn from a traced picture first, under all that stands for something,
    // and in ink: it is the drawing's own lines, never the plate, however much of the plan they span.
    floorPlan.floorBackdrop.filter(isAsDrawn).forEach((feature) => {
      commands.push({
        kind: 'area',
        layer: 'floor',
        id: feature.id,
        coordinates: feature.coordinates,
        fill: AS_DRAWN_FILL,
        fillOpacity: 1,
        stroke: null,
        strokeWidth: 0,
      });
    });

    const structure = floorPlan.floorBackdrop.filter((feature) => !isAsDrawn(feature));
    backdropDrawOrder(structure, computeBoundingBox(floorPlan)).forEach(({ feature, isPlate }) => {
      commands.push({
        kind: 'area',
        layer: 'floor',
        id: feature.id,
        coordinates: feature.coordinates,
        fill: isPlate ? PLATE_FILL : WALL_FILL,
        fillOpacity: 1,
        stroke: isPlate ? PLATE_STROKE : WALL_STROKE,
        strokeWidth: isPlate ? 0.5 : 0.7,
      });
    });

    // Floor detail drawn as lines rather than shapes. One plan keeps its entire floor drawing
    // this way, and skipping it left that plan with rooms and an outline and nothing between.
    floorPlan.floorLines.forEach((feature) => {
      commands.push({
        kind: 'polyline',
        layer: 'floor',
        id: feature.id,
        paths: feature.paths,
        stroke: WALL_STROKE,
        strokeWidth: 0.7,
      });
    });
  }

  // Rooms after the floor, so a room always reads in front of the walls bounding it. Only the
  // fill is held back: at full strength it buried the structure underneath, which is the state
  // the plans with the most rooms were in.
  if (options.showRooms) {
    floorPlan.rooms.forEach((feature) => {
      commands.push({
        kind: 'area',
        layer: 'room',
        id: feature.id,
        coordinates: feature.coordinates,
        fill: ROOM_FILL,
        fillOpacity: ROOM_FILL_OPACITY,
        stroke: ROOM_STROKE,
        strokeWidth: 1,
      });
    });
  }

  if (options.showBorder) {
    floorPlan.border.forEach((feature) => {
      commands.push({
        kind: 'area',
        layer: 'border',
        id: feature.id,
        coordinates: feature.coordinates,
        fill: null,
        fillOpacity: 1,
        stroke: layerPalette.wallEdge,
        strokeWidth: 1.6,
      });
    });
  }

  if (options.showDeskShapes) {
    floorPlan.deskShapes.forEach((shape) => {
      const key = selectionKeyOf(shape);
      const selected = options.selectedKeys.has(key);
      const hovered = options.hoveredKey === key;
      // Asked for only when there is a name. `iconPathOf` is a lookup the caller supplies, and
      // one that answered for a null name would silently replace every outline on the plan.
      const iconPath = shape.iconName ? options.iconPathOf(shape.iconName) : undefined;

      // Drawn as a glyph in place of its outline when one is chosen. The outline is still what
      // is stored and still what is selected and dragged — the icon is how it is *drawn*, and
      // a client that knows nothing about icons draws the footprint, which is the truth of
      // where the furniture is.
      if (iconPath) {
        const box = extentOfRings(shape.coordinates);

        commands.push({
          kind: 'icon',
          layer: 'deskShape',
          id: shape.id,
          at: [(box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2],
          rotation: shape.rotation,
          path: iconPath,
          // The furniture's own footprint, so a 1.6 m desk drawn as a monitor is 1.6 m of
          // monitor rather than a fixed glyph floating over the plan — converted into the
          // surface units an icon is drawn in, which is what this used to get wrong.
          size: toSurface(Math.max(box.maxX - box.minX, box.maxY - box.minY), options),
          color: selected || hovered ? layerPalette.selection : DESK_SHAPE_STROKE,
          outlined: selected || hovered,
          placeholder: false,
        });

        return;
      }

      commands.push({
        kind: 'area',
        layer: 'deskShape',
        id: shape.id,
        coordinates: shape.coordinates,
        fill: selected || hovered ? HIGHLIGHT_FILL : DESK_SHAPE_FILL,
        fillOpacity: 1,
        stroke: selected || hovered ? layerPalette.selection : DESK_SHAPE_STROKE,
        strokeWidth: emphasis(selected, hovered, 1.5, 1.2, 0.7),
      });
    });
  }

  if (options.showDesks) {
    floorPlan.desks
      // Filtered here, at the point of drawing, and nowhere else. The plan itself is
      // untouched, so a hidden desk is still saved, still selectable once shown, and still has
      // its geometry if the retirement is ever cancelled.
      .filter((desk) => options.showRetiredDesks || !options.retiredDeskIds?.has(desk.featureId))
      .forEach((desk) => commands.push(deskCommandOf(desk, options)));
  }

  // Annotations wait for the icon paths: drawing them first would flash a placeholder over
  // every valid icon while the module loads.
  if (options.showLabels && options.iconsLoaded) {
    floorPlan.labels.forEach((label) => commands.push(labelCommandOf(label, options)));
  }

  return commands;
};

export default buildDrawPlan;
